import type {
  RuleBoxCategoryFilter,
  RuleBoxFacets,
  RuleBoxOptionFilter,
} from "@loot-os/api-contract";
import {
  categoryKeyLabel,
  removeCategoryFilter,
  removeOptionFilter,
  setCategoryFilter,
  setOptionFilter,
  textChoices,
} from "@loot-os/api-contract/shopifyRuleBoxForm";
import { Button } from "@loot-os/ui/components/button";
import { Label } from "@loot-os/ui/components/label";
import { Plus, X } from "lucide-react";

import { RuleChoiceField } from "./RuleChoiceField";

/** One filter group's picker with its Remove button. */
function GroupRow({
  disabled,
  onRemove,
  removeLabel,
  children,
}: {
  disabled: boolean;
  onRemove: () => void;
  removeLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">{children}</div>
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={removeLabel}
          onClick={onRemove}
        >
          <X />
        </Button>
      )}
    </div>
  );
}

/** The buttons that add a filter group, one per name not yet filtered. */
function AddGroupButtons({
  label,
  names,
  disabled,
  onAdd,
}: {
  label: string;
  names: { id: string; label: string }[];
  disabled: boolean;
  onAdd: (id: string) => void;
}) {
  if (names.length === 0 || disabled) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      {names.map((name) => (
        <Button
          key={name.id}
          type="button"
          variant="outline"
          size="xs"
          className="font-normal"
          onClick={() => onAdd(name.id)}
        >
          <Plus />
          {name.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * The variant option filters (Size: M or L), the twin of the embedded admin's: one field per
 * option filtered, and a button per option the shop's variants have to add another.
 */
export function OptionFilterFields({
  facets,
  filters,
  disabled,
  onChange,
}: {
  facets: RuleBoxFacets | null;
  filters: RuleBoxOptionFilter[];
  disabled: boolean;
  onChange: (next: RuleBoxOptionFilter[]) => void;
}) {
  const options = facets?.options ?? [];
  const taken = new Set(filters.map((f) => f.name.trim().toLowerCase()));
  return (
    <div className="flex flex-col gap-3">
      <Label>Variant options</Label>
      {filters.map((filter) => (
        <GroupRow
          key={filter.name}
          disabled={disabled}
          removeLabel={`Remove the ${filter.name} filter`}
          onRemove={() => onChange(removeOptionFilter(filters, filter.name))}
        >
          <RuleChoiceField
            id={`rule-option-${filter.name}`}
            label={filter.name}
            description={`Only variants with one of these ${filter.name.toLowerCase()} values.`}
            noun="values"
            options={textChoices(
              options.find((o) => o.name.trim().toLowerCase() === filter.name.trim().toLowerCase())
                ?.values ?? [],
            )}
            chosen={filter.values}
            freeText={false}
            disabled={disabled}
            onChange={(values) => onChange(setOptionFilter(filters, filter.name, values))}
          />
        </GroupRow>
      ))}
      <AddGroupButtons
        label="Filter by"
        names={options
          .filter((o) => !taken.has(o.name.trim().toLowerCase()))
          .map((o) => ({ id: o.name, label: o.name }))}
        disabled={disabled}
        onAdd={(name) => onChange(setOptionFilter(filters, name, []))}
      />
      {options.length === 0 && filters.length === 0 && (
        <p className="text-sm text-muted-foreground">The shop's products have no options.</p>
      )}
    </div>
  );
}

/**
 * The category attribute filters (Color: Black), the twin of the embedded admin's. They are read
 * only once the merchant grants category access in the Shopify app, which the dashboard cannot
 * ask for.
 */
export function CategoryFilterFields({
  facets,
  filters,
  disabled,
  onChange,
}: {
  facets: RuleBoxFacets | null;
  filters: RuleBoxCategoryFilter[];
  disabled: boolean;
  onChange: (next: RuleBoxCategoryFilter[]) => void;
}) {
  const attributes = facets?.categoryAttributes ?? [];
  const taken = new Set(filters.map((f) => f.key));
  return (
    <div className="flex flex-col gap-3">
      <Label>Category attributes</Label>
      {filters.map((filter) => {
        const facet = attributes.find((a) => a.key === filter.key);
        const name = facet?.label ?? categoryKeyLabel(filter.key);
        return (
          <GroupRow
            key={filter.key}
            disabled={disabled}
            removeLabel={`Remove the ${name} filter`}
            onRemove={() => onChange(removeCategoryFilter(filters, filter.key))}
          >
            <RuleChoiceField
              id={`rule-category-${filter.key}`}
              label={name}
              description={`Only products whose ${name.toLowerCase()} is one of these.`}
              noun="values"
              options={facet?.values ?? []}
              chosen={filter.values}
              freeText={false}
              disabled={disabled}
              missingLabel="Removed value"
              onChange={(values) => onChange(setCategoryFilter(filters, filter.key, values))}
            />
          </GroupRow>
        );
      })}
      <AddGroupButtons
        label="Filter by"
        names={attributes
          .filter((a) => !taken.has(a.key))
          .map((a) => ({ id: a.key, label: a.label }))}
        disabled={disabled}
        onAdd={(key) => onChange(setCategoryFilter(filters, key, []))}
      />
      {facets && !facets.categoryValuesNamed && (
        <p className="text-sm text-muted-foreground">
          The merchant allows category attributes from the box builder in the Shopify app.
        </p>
      )}
      {facets?.categoryValuesNamed && attributes.length === 0 && filters.length === 0 && (
        <p className="text-sm text-muted-foreground">
          None of the shop's products have category attributes yet.
        </p>
      )}
    </div>
  );
}
